import { Component, ChangeDetectionStrategy, ViewEncapsulation, effect, inject, input, output, signal, ViewChild } from '@angular/core';
import { CommonModule, formatDate } from '@angular/common';
import { ButtonsModule } from '@progress/kendo-angular-buttons';
import { IconsModule } from '@progress/kendo-angular-icons';
import { PDFExportModule, PDFExportComponent } from '@progress/kendo-angular-pdf-export';
import { SVGIcon, exportIcon } from '@progress/kendo-svg-icons';
import { ActivatedRoute } from '@angular/router';
import { OrganizationService } from '../../../features/admin/organizations/services/organization.service';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { BaseComponent } from '../../shared/common/base.component';
import { filter } from 'rxjs/operators';

@Component({
  selector: 'app-base-analytics-page',
  templateUrl: './base-analytics-page.component.html',
  styleUrls: ['./base-analytics-page.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  encapsulation: ViewEncapsulation.None,
  standalone: true,
  imports: [CommonModule, ButtonsModule, IconsModule, PDFExportModule],
})
export class BaseAnalyticsPageComponent extends BaseComponent {
  @ViewChild('pdf', { static: true }) pdf!: PDFExportComponent;

  // Services
  private readonly route = inject(ActivatedRoute);
  private readonly orgService = inject(OrganizationService);

  // Common inputs
  pageTitle = input<string>('');
  reportTitle = input<string>('Analytics Report');
  showExportButton = input<boolean>(true);
  exportButtonSize = input<'small' | 'medium' | 'large'>('small');
  exportButtonTheme = input<'primary' | 'secondary' | 'tertiary'>('primary');

  // Layout control inputs
  showFilterHeader = input<boolean>(false);
  showFilterExportButton = input<boolean>(false);
  showMetricCardsGrid = input<boolean>(false);
  showChartGrid = input<boolean>(false);

  // Common outputs
  onExport = output<void>();
  onPdfExport = output<string>(); // Emit filename for child components

  // SVG Icons
  protected readonly exportIcon: SVGIcon = exportIcon;

  // Organization state
  protected organizationName = signal<string>('Unknown');

  constructor() {
    super();
    this.initEffect();
  }

  private initEffect() {
    effect(() => {
      this.route.queryParams
        .pipe(
          filter((params) => !!params['organizationId']),
          takeUntilDestroyed(this.destroyRef)
        )
        .subscribe({
          next: (params) => {
            this.orgService.getOrganizationById(+params['organizationId']).subscribe({
              next: (response) => this.organizationName.set(response.name ?? 'Unknown'),
              error: (error) => {
                console.error('Error fetching Organization Name:', error);
                this.organizationName.set('Unknown');
              },
            });
          },
        });
    });
  }

  // Get current date formatted for file name
  private get shortDate(): string {
    return formatDate(new Date(), 'yyyyMMdd', 'en-US');
  }

  // Generate file name based on organization, report title and date
  private get fileName(): string {
    return `${this.organizationName()}_${this.reportTitle()}_${this.shortDate}.pdf`;
  }

  // Common methods
  protected exportToPdf(): void {
    this.onExport.emit();
    
    // Emit filename for child components to handle their own PDF export
    this.onPdfExport.emit(this.fileName);
  }

  // Utility method for page breaks
  protected addPageBreak(): void {
    // This can be called from child components to add page breaks
    // The actual page break is handled by CSS with .page-break class
  }
}
